/**
 * The ad wall's artwork: ten real client ads on one atlas.
 *
 * Each creative owns one slot. Its poster is drawn in first, from a few KB of
 * JPEG, so the wall lights up at once; once its video decodes, every new
 * frame is copied into the same slot (see `adWallPlayer`), and each card on
 * the wall that shows it plays. The shader decides which slot a wall cell
 * samples, so the atlas never holds more than the ten cards and is patched a
 * slot at a time rather than built.
 *
 * The slot is 9:16 because the creatives are. A card that is not — ElevenLabs
 * ships square — is letterboxed on a dark card, which is how a feed shows it;
 * cropping an ad to fill a card takes off the caption or the button, which
 * is the part that makes it look like an ad.
 *
 * The inset, the corner radius and the hairline are drawn into the slot, not
 * in the shader: the slot's transparent border is what gives the wall its
 * gaps, and a video frame is drawn through the same clip as the poster so the
 * two line up to the pixel. The feed chrome (`adWallChrome`) is stamped over
 * both, so a card is a creative inside a phone's feed rather than a clip.
 *
 * The atlas is theme-independent on purpose. An ad does not change colour
 * when the reader flips to dark, so only the shader's background and glow
 * uniforms move on a theme change.
 */

export type Creative = { slug: string; brand: string };

/** The files live at `public/one/ad-wall/<slug>.{jpg,mp4}`. */
export const CREATIVES: readonly Creative[] = [
  { slug: "acorns", brand: "Acorns" },
  { slug: "kalshi", brand: "Kalshi" },
  { slug: "elevenlabs", brand: "ElevenLabs" },
  { slug: "coffee-meets-bagel", brand: "Coffee Meets Bagel" },
  { slug: "pogo", brand: "Pogo" },
  { slug: "betr", brand: "Betr" },
  { slug: "perplexity", brand: "Perplexity" },
  { slug: "bluechew", brand: "BlueChew" },
  { slug: "juicebox", brand: "Juicebox" },
  { slug: "pogo-outdoor", brand: "Pogo" },
];

export function creativeSrc(creative: Creative, kind: "jpg" | "mp4") {
  return `/one/ad-wall/${creative.slug}.${kind}`;
}

/**
 * Slot geometry, in texture pixels.
 *
 * The card inside the inset is exactly 360×640, so a 9:16 creative covers it
 * with no crop. 360 is a little under the 380 device pixels a card gets on a
 * 2× display, which is as sharp as a 406-wide source video can be anyway.
 */
export const SLOT_W = 396;
export const SLOT_H = 676;
const INSET = 18;
const RADIUS = 28;
export const COLS = 5;
export const ROWS = 2;
export const TEX_W = SLOT_W * COLS;
export const TEX_H = SLOT_H * ROWS;

/** Behind a letterboxed creative. A feed's bars, not the page's background. */
const LETTERBOX = "#141414";

/**
 * How far from the card's shape a creative can be before it is letterboxed
 * rather than cropped. The 9:16 sources differ from the card by well under a
 * percent; anything this far off would lose real picture to a crop.
 */
const COVER_TOLERANCE = 0.06;

type Ctx = CanvasRenderingContext2D;
type Source = HTMLImageElement | HTMLVideoElement;

export function slotOrigin(index: number): { x: number; y: number } {
  return {
    x: (index % COLS) * SLOT_W,
    y: Math.floor(index / COLS) * SLOT_H,
  };
}

/** The card inside a slot whose top-left is (x, y). */
export function cardRect(x: number, y: number) {
  return {
    x: x + INSET,
    y: y + INSET,
    w: SLOT_W - INSET * 2,
    h: SLOT_H - INSET * 2,
  };
}

function sourceSize(source: Source): [number, number] {
  if (source instanceof HTMLVideoElement)
    return [source.videoWidth, source.videoHeight];
  return [source.naturalWidth, source.naturalHeight];
}

function rr(c: Ctx, x: number, y: number, w: number, h: number, r: number) {
  c.beginPath();
  c.moveTo(x + r, y);
  c.arcTo(x + w, y, x + w, y + h, r);
  c.arcTo(x + w, y + h, x, y + h, r);
  c.arcTo(x, y + h, x, y, r);
  c.arcTo(x, y, x + w, y, r);
  c.closePath();
}

/**
 * Draw one creative as a card, with the slot's top-left at (x, y).
 *
 * Clears the whole slot first, so the border stays transparent whatever was
 * there before. With no source (a poster that failed to load) the card is a
 * plain dark one, which on the wall reads as an ad still arriving rather than
 * a hole. `chrome` is that creative's feed layer, drawn last under the same
 * clip; it is a slot-sized bitmap, so it lands by the slot's own origin.
 */
export function drawCard(
  c: Ctx,
  source: Source | null,
  x: number,
  y: number,
  chrome: HTMLCanvasElement | null,
) {
  c.clearRect(x, y, SLOT_W, SLOT_H);
  const { x: cx, y: cy, w: cw, h: ch } = cardRect(x, y);

  c.save();
  rr(c, cx, cy, cw, ch, RADIUS);
  c.clip();
  c.fillStyle = LETTERBOX;
  c.fillRect(cx, cy, cw, ch);
  if (source) {
    const [sw, sh] = sourceSize(source);
    if (sw > 0 && sh > 0) {
      const card = cw / ch;
      const cover = Math.abs(sw / sh - card) / card < COVER_TOLERANCE;
      const scale = cover
        ? Math.max(cw / sw, ch / sh)
        : Math.min(cw / sw, ch / sh);
      const dw = sw * scale;
      const dh = sh * scale;
      c.drawImage(source, cx + (cw - dw) / 2, cy + (ch - dh) / 2, dw, dh);
    }
  }
  if (chrome) c.drawImage(chrome, x, y);
  c.restore();

  // A hairline so a pale frame still reads on the light ground. Invisible on
  // dark, which is why it is drawn into the slot rather than handled as a
  // theme uniform.
  c.strokeStyle = "rgba(0,0,0,0.06)";
  c.lineWidth = 1;
  rr(c, cx + 0.5, cy + 0.5, cw - 1, ch - 1, RADIUS);
  c.stroke();
}

/**
 * Fetch one poster. A failure resolves to null rather than rejecting: the
 * card is drawn plain, and the video can still take the slot.
 */
export function loadPoster(
  creative: Creative,
): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.decoding = "async";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = creativeSrc(creative, "jpg");
  });
}
